import { useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types/commons';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { exportRoadmapList } from '~services/route';
import { RouteRoadmapListParams } from '~services/route/types';
import { downloadFileByRequest } from '~utils/download';
import { formatterDate } from '~utils/formatter';

interface DownloadRouteRoadmapListOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface DownloadRouteRoadmapListReturn {
  loading: boolean;
  onDownloadRoadmap: (params: RouteRoadmapListParams) => Promise<void>;
}

export const useDownloadRouteRoadmapList = (
  options?: DownloadRouteRoadmapListOptions,
): DownloadRouteRoadmapListReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const currentParams = useRef<Nullable<RouteRoadmapListParams>>(null);

  const [, loadingExportRoadmapList, , executeExportRoadmapList] =
    useLazyRequest({
      request: exportRoadmapList,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const isMultiple = currentParams.current
            ? currentParams.current.routeIds.length > 1
            : false;
          const prefixName = isMultiple ? 'hojas-de-rutas' : 'hoja-de-ruta';
          const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });
          const extension = currentParams.current?.excelMode ? 'xlsx' : 'pdf';

          const defaultFileName = `${prefixName}-${currentDate}.${extension}`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(
            `Se ha exportado el/los documentos de ruta correctamente.`,
            { variant: 'success' },
          );

          options?.onSuccess?.();
        } else {
          enqueueSnackbar(
            'No se pudo exportar el/los documentos de ruta, intente nuevamente.',
            { variant: 'error' },
          );

          options?.onError?.();
        }
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo exportar el/los documentos de ruta, intente nuevamente.',
          { variant: 'error' },
        );

        options?.onError?.();
      },
    });

  const onDownloadRoadmap = useCallback(
    async (params: RouteRoadmapListParams) => {
      currentParams.current = params;
      await executeExportRoadmapList(params);
      currentParams.current = null;
    },
    [executeExportRoadmapList],
  );

  return { loading: loadingExportRoadmapList, onDownloadRoadmap };
};
